(() => {
  const formatNumber = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const initFitMatch = (root) => {
    if (!root || root.dataset.gonzFitReady === 'true') return;

    const fitSection = root.querySelector('[data-gonz-fit]');
    const inputs = {
      lens: root.querySelector('[data-fit-input="lens"]'),
      bridge: root.querySelector('[data-fit-input="bridge"]'),
      temple: root.querySelector('[data-fit-input="temple"]'),
    };
    const prompt = root.querySelector('[data-fit-prompt]');
    const answer = root.querySelector('[data-fit-answer]');
    const title = root.querySelector('[data-fit-title]');
    const copy = root.querySelector('[data-fit-copy]');

    if (!fitSection || !inputs.lens || !inputs.bridge || !inputs.temple || !answer || !title || !copy) return;

    const targets = {
      lens: Number(root.dataset.lensTarget),
      bridge: Number(root.dataset.bridgeTarget),
      temple: Number(root.dataset.templeTarget),
    };

    if (Object.values(targets).some((value) => !Number.isFinite(value) || value <= 0)) return;

    root.dataset.gonzFitReady = 'true';
    const tolerances = { lens: 2, bridge: 2, temple: 5 };

    const updateRow = (key, value) => {
      const row = root.querySelector('[data-fit-result="' + key + '"]');
      if (!row) return;

      const measurement = row.querySelector('dd');
      const comparison = row.querySelector('span');
      const delta = Math.abs(value - targets[key]);
      const close = delta <= tolerances[key];

      if (measurement) measurement.textContent = formatNumber(value) + ' mm';
      if (comparison) {
        comparison.textContent = close ? 'Close' : formatNumber(delta) + ' mm difference';
        comparison.classList.toggle('is-close', close);
        comparison.classList.toggle('is-different', !close);
      }
    };

    const update = () => {
      const values = {
        lens: Number(inputs.lens.value),
        bridge: Number(inputs.bridge.value),
        temple: Number(inputs.temple.value),
      };
      const valid = Object.values(values).every((value) => Number.isFinite(value) && value > 0);

      if (!valid) {
        answer.hidden = true;
        if (prompt) prompt.hidden = false;
        return;
      }

      const close = {
        lens: Math.abs(values.lens - targets.lens) <= tolerances.lens,
        bridge: Math.abs(values.bridge - targets.bridge) <= tolerances.bridge,
        temple: Math.abs(values.temple - targets.temple) <= tolerances.temple,
      };
      const score = Object.values(close).filter(Boolean).length;

      answer.classList.remove('is-good', 'is-mixed', 'is-different');
      if (score === 3) {
        answer.classList.add('is-good');
        title.textContent = 'Very similar measurements';
        copy.textContent = 'The three dimensions are closely aligned with your current pair. Frame geometry and personal preference can still change how it feels.';
      } else if (score > 0) {
        answer.classList.add('is-mixed');
        title.textContent = 'Some measurements differ';
        copy.textContent = 'Check the individual comparisons below. A difference in bridge or temple length can be as noticeable as lens width.';
      } else {
        answer.classList.add('is-different');
        title.textContent = 'Noticeably different measurements';
        copy.textContent = 'This pair is likely to feel different from your current frame. Consider an in-store try-on or contact us for guidance.';
      }

      Object.keys(values).forEach((key) => updateRow(key, values[key]));
      if (prompt) prompt.hidden = true;
      answer.hidden = false;
    };

    Object.values(inputs).forEach((input) => input.addEventListener('input', update, { passive: true }));
  };

  const whenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1200 });
    } else {
      window.setTimeout(callback, 80);
    }
  };

  const hydrateImage = (image) => {
    if (!image || image.dataset.loaded === 'true') return Promise.resolve(image);

    if (image.dataset.loading === 'true') {
      return new Promise((resolve) => {
        image.addEventListener('gonz:reel-image-ready', () => resolve(image), { once: true });
      });
    }

    image.dataset.loading = 'true';

    return new Promise((resolve) => {
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        image.dataset.loaded = 'true';
        delete image.dataset.loading;
        image.dispatchEvent(new CustomEvent('gonz:reel-image-ready'));
        resolve(image);
      };

      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });

      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        image.removeAttribute('data-srcset');
      }
      if (image.dataset.src) {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      }
      if (image.complete && image.currentSrc) finish();
    });
  };

  const initProductViewReel = (reel) => {
    if (!reel || reel.dataset.gonzReelReady === 'true') return;

    const images = Array.from(reel.querySelectorAll('[data-gonz-reel-image]'));
    if (images.length < 2) return;

    const stage = reel.querySelector('[data-reel-stage]');
    const previous = reel.querySelector('[data-reel-prev]');
    const next = reel.querySelector('[data-reel-next]');
    const toggle = reel.querySelector('[data-reel-toggle]');
    const toggleIcon = reel.querySelector('[data-reel-toggle-icon]');
    const dots = reel.querySelector('[data-reel-dots]');
    const status = reel.querySelector('[data-reel-status]');

    if (!stage || !previous || !next || !toggle || !dots) return;

    reel.dataset.gonzReelReady = 'true';

    const interval = Math.max(2200, Number(reel.dataset.reelInterval) || 2800);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = 0;
    let timer = 0;
    let inRange = false;
    let userPaused = false;
    let changeToken = 0;
    let touchStart = null;

    const isPaused = () => (
      userPaused ||
      reducedMotion.matches ||
      !inRange ||
      document.hidden
    );

    const updateToggle = () => {
      toggle.hidden = reducedMotion.matches;
      const paused = userPaused || reducedMotion.matches;
      toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      toggle.setAttribute('aria-label', paused ? 'Play product views' : 'Pause product views');
      if (toggleIcon) toggleIcon.textContent = paused ? '▶' : 'Ⅱ';
    };

    const updateUi = () => {
      images.forEach((image, index) => {
        const active = index === activeIndex;
        image.classList.toggle('is-active', active);
        image.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      dots.querySelectorAll('button').forEach((dot, index) => {
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });

      if (status) status.textContent = 'View ' + (activeIndex + 1) + ' of ' + images.length;
    };

    const clearTimer = () => {
      if (!timer) return;
      window.clearTimeout(timer);
      timer = 0;
    };

    const schedule = () => {
      clearTimer();
      if (isPaused()) return;
      timer = window.setTimeout(() => show(activeIndex + 1), interval);
    };

    const loadAhead = () => {
      if (!inRange) return;
      const following = images[(activeIndex + 1) % images.length];
      whenIdle(() => hydrateImage(following));
    };

    const show = (requestedIndex) => {
      const targetIndex = (requestedIndex + images.length) % images.length;
      const token = ++changeToken;
      clearTimer();

      hydrateImage(images[targetIndex]).then(() => {
        if (token !== changeToken) return;
        activeIndex = targetIndex;
        updateUi();
        loadAhead();
        schedule();
      });
    };

    images.forEach((image, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gonz-sun-reel__dot';
      dot.setAttribute('aria-label', 'Show product view ' + (index + 1) + ' of ' + images.length);
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => show(index));
      dots.appendChild(dot);
    });

    previous.addEventListener('click', () => show(activeIndex - 1));
    next.addEventListener('click', () => show(activeIndex + 1));
    toggle.addEventListener('click', () => {
      userPaused = !userPaused;
      updateToggle();
      schedule();
    });

    stage.addEventListener('touchstart', (event) => {
      const point = event.changedTouches[0];
      touchStart = point ? { x: point.clientX, y: point.clientY } : null;
    }, { passive: true });

    stage.addEventListener('touchend', (event) => {
      if (!touchStart) return;
      const point = event.changedTouches[0];
      if (!point) return;
      const deltaX = point.clientX - touchStart.x;
      const deltaY = point.clientY - touchStart.y;
      touchStart = null;

      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      show(activeIndex + (deltaX < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('visibilitychange', schedule, { passive: true });

    const handleMotionPreference = () => {
      updateToggle();
      schedule();
    };
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', handleMotionPreference);
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== reel) return;
          inRange = entry.isIntersecting;
          if (inRange) {
            loadAhead();
            schedule();
          } else {
            clearTimer();
          }
        });
      }, { rootMargin: '280px 0px 280px', threshold: 0.01 });
      observer.observe(reel);
    } else {
      inRange = true;
      loadAhead();
      schedule();
    }

    updateToggle();
    updateUi();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gonz-sun-content]').forEach(initFitMatch);
    scope.querySelectorAll('[data-gonz-reel]').forEach(initProductViewReel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
